import { PieceType, AIConfig } from '../types';

// Game configuration
export const GAME_CONFIG = {
  WIDTH: 720,
  HEIGHT: 1280,
  TITLE: 'ChessRogue',
  VERSION: '0.1.0',
};

// Board configuration
export const BOARD_CONFIG = {
  CELL_SIZE: 80,
  PADDING: 40,
  COLORS: {
    LIGHT: 0xf0d9b5,
    DARK: 0xb58863,
    HIGHLIGHT: 0x7fc97f,
    SELECTED: 0xfff68f,
    VALID_MOVE: 0x90EE90,
    ATTACK: 0xff6b6b,
  },
};

// Piece health values
export const PIECE_HEALTH: Record<PieceType, number> = {
  p: 1,  // Pawn
  n: 2,  // Knight
  b: 2,  // Bishop
  r: 3,  // Rook
  q: 4,  // Queen
  k: 5,  // King (not used in standard gameplay)
};

// AI difficulty configurations by floor
export const AI_DIFFICULTY: Record<number, AIConfig> = {
  1: { depth: 2, moveTime: 100, difficulty: 'easy' },
  2: { depth: 2, moveTime: 100, difficulty: 'easy' },
  3: { depth: 2, moveTime: 150, difficulty: 'easy' },
  4: { depth: 3, moveTime: 150, difficulty: 'easy' },
  5: { depth: 4, moveTime: 200, difficulty: 'medium' },
  6: { depth: 4, moveTime: 200, difficulty: 'medium' },
  7: { depth: 4, moveTime: 250, difficulty: 'medium' },
  8: { depth: 5, moveTime: 300, difficulty: 'medium' },
  9: { depth: 6, moveTime: 500, difficulty: 'hard' },
  10: { depth: 6, moveTime: 500, difficulty: 'hard' },
  11: { depth: 6, moveTime: 600, difficulty: 'hard' },
  12: { depth: 8, moveTime: 2000, difficulty: 'expert' }, // Boss
};

// Floor progression
export const FLOOR_PROGRESSION = {
  TOTAL_FLOORS: 12,
  ACTS: 3,
  FLOORS_PER_ACT: 4,
  BOARD_SIZES: {
    ACT1: 6,  // 6x6 boards (floors 1-7)
    ACT2: 6,  // 6x6 boards (floors 1-7)
    ACT3: 7,  // 7x7 boards (floors 8-11)
    BOSS: 8,  // 8x8 for boss (floor 12)
  },
};

// Encounter weights for floor generation
export const ENCOUNTER_WEIGHTS = {
  combat: 50,
  elite: 10,
  puzzle: 15,
  shop: 10,
  rest: 10,
  mystery: 5,
};

// Starting configuration
export const STARTING_CONFIG = {
  PIECE: 'n' as PieceType, // Start with Knight
  GOLD: 0,
  FLOOR: 1,
};

// Colors
export const COLORS = {
  PRIMARY: 0x2c3e50,
  SECONDARY: 0x3498db,
  SUCCESS: 0x2ecc71,
  DANGER: 0xe74c3c,
  WARNING: 0xf39c12,
  WHITE: 0xffffff,
  BLACK: 0x000000,
  BACKGROUND: 0x2c3e50,
  UI_BG: 0x34495e,
  UI_TEXT: 0xecf0f1,
  BUTTON: 0x3498db,
  BUTTON_HOVER: 0x5dade2,
};

// Touch target sizes (mobile-first)
export const TOUCH_CONFIG = {
  MIN_TARGET_SIZE: 64, // 64px minimum (exceeds 44px requirement)
  BUTTON_PADDING: 16,
  DRAG_THRESHOLD: 10,
};

// Animation durations (milliseconds)
export const ANIMATION_DURATION = {
  PIECE_MOVE: 300,
  PIECE_CAPTURE: 200,
  FADE_IN: 200,
  FADE_OUT: 200,
  BUTTON_PRESS: 100,
};

// Z-depths for rendering order
export const Z_DEPTHS = {
  BOARD: 0,
  OBSTACLES: 5,
  PIECES: 10,
  HIGHLIGHTS: 8,
  UI: 100,
  MODAL: 200,
};

// Storage keys
export const STORAGE_KEYS = {
  SAVE_DATA: 'chessrogue_save',
  SETTINGS: 'chessrogue_settings',
  UNLOCKS: 'chessrogue_unlocks',
};

// Scene keys
export const SCENES = {
  BOOT: 'BootScene',
  PRELOAD: 'PreloadScene',
  MENU: 'MenuScene',
  MAP: 'MapScene',
  BATTLE: 'BattleScene',
  PUZZLE: 'PuzzleScene',
  SHOP: 'ShopScene',
  HEAL: 'HealScene',
  TREASURE: 'TreasureScene',
  FLOOR_TRANSITION: 'FloorTransitionScene',
  REWARD: 'RewardScene',
  BOSS_INTRO: 'BossIntroScene',
  RUN_SUMMARY: 'RunSummaryScene',
  SETTINGS: 'SettingsScene',
  DEBUG: 'DebugScene',
};

// Asset paths
export const ASSETS = {
  PIECES: {
    wp: 'assets/pieces/wp.png',
    wn: 'assets/pieces/wn.png',
    wb: 'assets/pieces/wb.png',
    wr: 'assets/pieces/wr.png',
    wq: 'assets/pieces/wq.png',
    wk: 'assets/pieces/wk.png',
    bp: 'assets/pieces/bp.png',
    bn: 'assets/pieces/bn.png',
    bb: 'assets/pieces/bb.png',
    br: 'assets/pieces/br.png',
    bq: 'assets/pieces/bq.png',
    bk: 'assets/pieces/bk.png',
  },
};

// Chess clock configuration
export const CHESS_CLOCK_CONFIG = {
  FLOOR_THRESHOLD: 1,      // Chess clock activates on all floors
  DEFAULT_TIME_MS: 300000,  // 5 minutes per side (base for floors 8+)
  // Time scaling per floor tier (ms per side)
  FLOOR_TIME: {
    EARLY: 600000,   // 10 minutes (floors 1-3)
    MID: 480000,     // 8 minutes (floors 4-7)
    LATE: 300000,    // 5 minutes (floors 8-11)
    BOSS: 600000,    // 10 minutes (floor 12 boss)
  },
};

// Performance targets
export const PERFORMANCE = {
  TARGET_FPS: 60,
  MIN_FPS: 30,
  MAX_AI_TIME: 1000, // Max 1 second for AI move
};
